/**
 * The scene model of docs/08-frontend-and-ux.md §4.1, as the layout sees it.
 *
 * Two node species and three edge species. The **union junction** is the idea
 * that makes a family chart work: a small anchor per union assertion, from which
 * the union bar reaches its two partners and the child drops descend. Children
 * hang from the junction of the union that co-parents them, so in a polygamous
 * household — Hassan with two wives — each wife's children hang from her own
 * junction and never appear under the wrong mother (§4.3).
 */

import type {
  FuzzyDate,
  ParentBasis,
  PersonSummary,
  TreeEdge,
  UnionEndReason,
  UnionType,
} from '@famtree/shared';

export interface LayoutOptions {
  /** Person card box. Bands are sized from it (§4.2 step 4). */
  cardWidth: number;
  cardHeight: number;
  /** Gap between cards inside a layer. */
  cardGap: number;
  /** Extra vertical space between bands, for the routing gutters of §4.2(5). */
  bandGap: number;
  /** Crossing-reduction sweeps (§4.2 step 3 asks for 8). */
  sweeps: number;
}

export const DEFAULT_LAYOUT: LayoutOptions = {
  cardWidth: 168,
  cardHeight: 72,
  cardGap: 28,
  bandGap: 96,
  sweeps: 8,
};

export interface SceneInput {
  focus: string;
  persons: PersonSummary[];
  edges: TreeEdge[];
}

/** A union as the chart draws it: the pair, its dates, and how it ended. */
export interface UnionJunction {
  id: string;
  a: string;
  b: string;
  unionType: UnionType | null;
  endReason: UnionEndReason | null;
  started: FuzzyDate | null;
  ended: FuzzyDate | null;
  /** Children hanging from this junction — both parents recorded and united. */
  children: string[];
  x: number;
  y: number;
}

/** A parent → child line. `junctionId` is null when the parents share no union. */
export interface ChildDrop {
  id: string;
  parentId: string;
  childId: string;
  junctionId: string | null;
  basis: ParentBasis;
  /** §4.5 — the edge spans more than one band and routes through the gutters. */
  crossBand: boolean;
  status: TreeEdge['status'];
}

export interface PlacedPerson {
  id: string;
  person: PersonSummary;
  generation: number;
  x: number;
  y: number;
}

export interface SiblingBracket {
  id: string;
  a: string;
  b: string;
}

export interface TreeLayout {
  persons: Map<string, PlacedPerson>;
  junctions: UnionJunction[];
  drops: ChildDrop[];
  brackets: SiblingBracket[];
  /** Generation index → band rectangle, for the tints and labels of §4.6. */
  bands: Array<{ generation: number; y: number; height: number }>;
  width: number;
  height: number;
  /** Layering constraints that could not be satisfied (§4.2 step 1). */
  violations: string[];
}
